import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ThemeContext, type ThemeValue } from './context';
import {
  DARK_SCHEME_QUERY,
  THEME_PREF_KEY,
  THEME_TRANSITION_MS,
  applyThemeColorMeta,
  getSystemPrefersDark,
  readThemePref,
  resolveTheme,
  writeThemePref,
  type ResolvedTheme,
  type ThemePref,
} from './theme';

/**
 * Owns the theme preference (cue-forge-c6b): resolves it to a concrete light/dark
 * value and mirrors that onto `<html data-theme>` so the token stylesheet
 * (:root[data-theme='light']) drives every subtree, including lazy chunks. While
 * the pref is 'system' it subscribes to prefers-color-scheme and re-resolves live
 * on an OS flip; an explicit choice unsubscribes and is persisted. A pre-paint
 * inline script sets data-theme before first paint (no flash); this provider is
 * idempotent with it.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readThemePref);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(getSystemPrefersDark);

  // Track the OS preference only while following the system.
  useEffect(() => {
    if (pref !== 'system') return;
    const mql = globalThis.matchMedia?.(DARK_SCHEME_QUERY);
    if (!mql) return;
    setSystemPrefersDark(mql.matches); // re-sync in case it changed before this ran
    const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [pref]);

  // Cross-tab sync (drt.22): another tab persisting a choice fires `storage`
  // here; re-read through the same guarded helper (malformed / cleared values
  // fall back to 'system') and route it into state, so the radiogroup,
  // <html data-theme> and the meta theme-color all follow via the effects below.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== THEME_PREF_KEY) return; // null = storage.clear()
      setPrefState(readThemePref());
    };
    globalThis.addEventListener?.('storage', onStorage);
    return () => globalThis.removeEventListener?.('storage', onStorage);
  }, []);

  const resolved = resolveTheme(pref, systemPrefersDark);

  // The theme actually painted so far. Lets the mirror effect distinguish a real
  // flip (arm the cross-fade) from the initial mount, where the pre-paint script
  // already rendered this theme and a transition would be wrong.
  const appliedTheme = useRef<ResolvedTheme | null>(null);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Mirror the resolved theme onto <html> for the token stylesheet to key off,
  // and onto <meta name="theme-color"> so browser chrome / PWA title bar follow.
  // On a genuine flip, briefly set [data-theme-transition] so tokens.css can
  // cross-fade every surface uniformly (drt.22) instead of letting the few
  // controls with their own transitions animate while the rest snaps.
  useEffect(() => {
    const root = document.documentElement;
    if (appliedTheme.current !== null && appliedTheme.current !== resolved) {
      root.setAttribute('data-theme-transition', '');
      clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => {
        root.removeAttribute('data-theme-transition');
      }, THEME_TRANSITION_MS);
    }
    appliedTheme.current = resolved;
    root.dataset.theme = resolved;
    applyThemeColorMeta(resolved);
  }, [resolved]);

  // On unmount, drop a pending transition-cleanup timer and the attribute with it.
  useEffect(
    () => () => {
      clearTimeout(transitionTimer.current);
      document.documentElement.removeAttribute('data-theme-transition');
    },
    [],
  );

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    writeThemePref(next);
  }, []);

  const value = useMemo<ThemeValue>(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
